import { computed, ref } from "vue";
import { defineStore } from "pinia";
import type { AuthUser, MenuCode } from "@pmc350/shared";
import { api, jsonBody } from "../api/client";

interface LoginResponse {
  token: string;
  expiresAt: string;
  mustChangePassword: boolean;
  user: AuthUser;
}

export const useAuthStore = defineStore("auth", () => {
  const user = ref<AuthUser | null>(null);
  const loading = ref(false);
  const authenticated = computed(() => Boolean(user.value && localStorage.getItem("pmc350_token")));
  const isSystemAdmin = computed(() => Boolean(user.value?.isSystemAdmin));
  const canWrite = computed(() => Boolean(user.value));
  const homePath = computed(() => {
    if (isSystemAdmin.value) return "/dashboard";
    const pathByMenu: Record<MenuCode, string> = {
      dashboard: "/dashboard",
      devices: "/devices",
      alarms: "/alarms",
      personnel: "/personnel",
      audit: "/audit",
      exports: "/exports",
      system_settings: "/settings",
      settings: "/settings",
      mqtt_gateway: "/mqtt-gateway",
      modbus_gateway: "/modbus-gateway",
      raw_protocol: "/raw-protocol",
    };
    const firstMenu = user.value?.menuKeys.find((key) => key !== "system_settings");
    return firstMenu ? pathByMenu[firstMenu] : "/no-access";
  });

  function canAccess(menuKey: MenuCode): boolean {
    return isSystemAdmin.value || Boolean(user.value?.menuKeys.includes(menuKey));
  }

  async function login(username: string, password: string): Promise<void> {
    loading.value = true;
    try {
      const result = await api<LoginResponse>("/auth/login", {
        method: "POST",
        body: jsonBody({ username, password }),
      });
      localStorage.setItem("pmc350_token", result.token);
      user.value = result.user;
    } finally {
      loading.value = false;
    }
  }

  async function restore(): Promise<boolean> {
    if (!localStorage.getItem("pmc350_token")) return false;
    try {
      user.value = await api<AuthUser>("/auth/me");
      return true;
    } catch {
      user.value = null;
      return false;
    }
  }

  async function logout(): Promise<void> {
    try {
      await api("/auth/logout", { method: "POST" });
    } finally {
      localStorage.removeItem("pmc350_token");
      user.value = null;
    }
  }

  return { user, loading, authenticated, isSystemAdmin, canWrite, homePath, canAccess, login, restore, logout };
});
